import { Button } from '@gmg/gmg-react-components';
import { FC, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FolderListItem } from '../../viewModels/Folder';
import FormElement from '../FormElement';
import { Options } from '../SelectElementWrapper';

export interface FolderModalProps {
    onClose?: () => void;
    onSubmit: (
        trimmedFolderName: string,
        groupIDsWithEditingRights: Array<string>,
        groupIDsWithViewingRights: Array<string>,
    ) => void;
    siblingFolderNames: Array<string>;
    defaultsForEdit?: {
        name: string;
        selectedViewerGroupIds: Array<string>;
        selectedEditorGroupIds: Array<string>;
    };
    availableGroups: Map<string, string>;
    assignedGroups: {
        editorGroups: Array<string>;
        readerGroups: Array<string>;
    };
    inheritedGroups: {
        editorGroups: Array<string>;
        readerGroups: Array<string>;
    };
    initialFolderName?: string;

    folderToEdit?: FolderListItem; // this is undefined in "create" use case
    isRoot: boolean;
};

const ButtonContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
    padding-right: 23px;
    & button {
        margin-left: 10px;
    }
`;

interface FolderModelForm {
    folderAccessViewerSelection: Array<Options>;
    folderAccessEditorSelection: Array<Options>;
    folderName: string;
}

const FolderModal: FC<FolderModalProps> = (props) => {

    const { t } = useTranslation();

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        setValue,
    } = useForm<FolderModelForm>({ mode: 'onBlur' });

    const registerOptionsForFolderName =
    {
        required: t('Validation.forms.field_required_val', 'Please fill out this field.'),
        maxLength: {
            value: 40,
            message: t('Validation.forms.maxLength_exceeded_val', 'You have exceeded the limit of {{maxCharacters}} characters.', {
                maxCharacters: 40,
            }),
        },
        validate: {
            folderNameIsUnique: (value: string) => !props.siblingFolderNames?.includes(value) || t('AddOrEditFolderModal.input_FolderName_duplicate_val', 'Oh no, a folder with this name already exists. Please try again.'),
        },
    };

    const selectedGroupsEditors = useWatch({
        control,
        name: 'folderAccessEditorSelection',
    });
    const selectedGroupsViewers = useWatch({
        control,
        name: 'folderAccessViewerSelection',
    });

    const selectedEditorOptions = selectedGroupsEditors ? selectedGroupsEditors.map((group: { value: string }) => group.value) : [];
    const selectedReaderOptions = selectedGroupsViewers ? selectedGroupsViewers.map((group: { value: string }) => group.value) : [];

    const availableEditorOptions = Array.from(props.availableGroups)
        // Selected reader groups must not be available in Editor Select box.
        .filter(([value, label]) => !selectedReaderOptions.includes(value))
        .map(([value, label]) => ({
            value,
            label,
            // Inherited groups cannot be selected/deselected by the user.
            isFixed: props.inheritedGroups.editorGroups.includes(value),
        }));
    const availableReaderOptions = Array.from(props.availableGroups)
        // Selected editor groups must not be available in Reader Select box.
        .filter(([value, label]) => !selectedEditorOptions.includes(value))
        .map(([value, label]) => ({
            value,
            label,
            // Inherited groups cannot be selected/deselected by the user.
            isFixed: props.inheritedGroups.readerGroups.includes(value),
        }));

    const initialEditorOptions = availableEditorOptions.filter(option =>
        (props.inheritedGroups.editorGroups.includes(option.value) ||
        props.assignedGroups.editorGroups.includes(option.value)));
    const initialReaderOptions = availableReaderOptions.filter(option =>
        (props.inheritedGroups.readerGroups.includes(option.value) ||
        props.assignedGroups.readerGroups.includes(option.value)));

    // Set default values as current control values. Otherwise, onSubmit will return undefined.
    useEffect(() => {
        setValue('folderAccessEditorSelection', initialEditorOptions);
        setValue('folderAccessViewerSelection', initialReaderOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = (formData: FolderModelForm) => {
        const trimmedFolderName = formData.folderName.trim();
        const groupIDsWithEditingRights = (formData.folderAccessEditorSelection || [])
            // Inherited groups are shown to the user for information, but are not written to the database.
            .filter(option => option.isFixed === false)
            .map(option => option.value);
        const groupIDsWithViewingRights = (formData.folderAccessViewerSelection || [])
            // Inherited groups are shown to the user for information, but are not written to the database.
            .filter(option => option.isFixed === false)
            .map(option => option.value);

        props.onSubmit(trimmedFolderName, groupIDsWithEditingRights, groupIDsWithViewingRights);
    };

    return (
        <Container>
            <form name="edit folder" onSubmit={handleSubmit(onSubmit)}>
                <FormElement
                    label={t('AddOrEditFolderModal.input_FolderName_label', 'Folder name')}
                    dataProperty="folderName"
                    placeholder={t('AddOrEditFolderModal.input_FolderName_desc', 'Enter folder name')}
                    registerElement={{
                        ...register('folderName', registerOptionsForFolderName),
                    }}
                    validationError={errors?.folderName?.message}
                    defaultValue={props.initialFolderName}
                />

                <ButtonContainer>
                    <Button
                        variant="primary"
                        type="submit"
                    >
                        {props.folderToEdit
                            ? t('ButtonTitles.action_apply_btn', 'Apply')
                            : t('ButtonTitles.action_add_btn', 'Add')}
                    </Button>
                    <Button
                        variant="secondary"
                        type="reset"
                        onClick={props.onClose}
                    >
                        {t('ButtonTitles.action_cancel_btn', 'Cancel')}
                    </Button>
                </ButtonContainer>
            </form>
        </Container>
    );
};

const Container = styled.div`
    width: 100%;

    form {
        width: 100%;
    }
`;


export default FolderModal;